import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import BillingDetailsForm, { BillingDetailsValue } from './BillingDetailsForm';
import { POLL_DELAYS_MS } from './activation';
import { hasNewTopupBatch } from './creditBatches';
import { checkoutErrorMessage, DETAILS_ERROR } from './checkoutErrors';
import { chargeSummary } from '../../utils/formatPrice';
import { useRazorpayCheckout } from './useRazorpayCheckout';
import { apiErrorDetail } from '../../utils/apiError';
import type {
  BillingProfile,
  CreditPack,
  TopupCheckoutSessionResponse,
} from '../../services/billingApi';
import type { MyCredits } from '../../services/creditsApi';

// Topup-specific overrides on top of checkoutErrors.ts's shared mapping —
// these two 409 codes (engine/app/api/checkout.py) only exist on this flow.
const TOPUP_ERROR_OVERRIDES: Record<string, string> = {
  credit_pack_not_purchasable: 'This credit pack is not available for purchase right now.',
  no_live_subscription_for_topup:
    'You need an active paid plan in this workspace before buying a top-up.',
};

/** The calls this dialog makes, injectable for tests. Production callers omit
 *  this — `resolveApi` below lazily `import()`s the real clients only once the
 *  flow actually runs, so this component's own module graph never touches
 *  Firebase (same test-seam pattern as FixPaymentDialog). */
export interface TopupModalApi {
  createTopupCheckout: (packCode: string) => Promise<TopupCheckoutSessionResponse>;
  getBillingProfile: () => Promise<BillingProfile>;
  saveBillingProfile: (value: BillingDetailsValue) => Promise<BillingProfile>;
  getMyCredits: () => Promise<MyCredits>;
}

interface TopupModalProps {
  open: boolean;
  /** Packs for ONE vertical, already fetched by PlanSummaryCard. */
  packs: CreditPack[];
  onClose: () => void;
  /** Fired once the poll confirms the new topup batch has landed. Caller
   *  refetches credits/invoices to refresh the meter immediately. */
  onGranted?: () => void;
  /** Test-only override. Left unset in production. */
  api?: TopupModalApi;
}

type Stage = 'select' | 'processing' | 'details' | 'granting' | 'timed_out';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * "Buy AI credits" — select a pack, pay via Razorpay, poll for the new
 * `topup` batch to land.
 *
 * Polling reuses UpgradeModal's exact `POLL_DELAYS_MS` schedule: a topup's
 * `credit_grant` push travels the SAME enqueue_push/drain_pushes pipeline as
 * every other push type, so it has the same proven, bounded latency (see
 * activation.ts's comment). `since` is captured right before checkout opens;
 * `hasNewTopupBatch` only matches a `topup` batch granted at/after it, so an
 * already-existing top-up from an earlier purchase can never short-circuit
 * the poll. If the backend's `batches` field is absent (older backend,
 * mid-deploy skew), there is nothing reliable to poll for — this degrades to
 * a single honest "credits will appear shortly" message with no poll,
 * exactly like the pre-batches gift-line fallback in CreditsMeter.
 */
const TopupModal: React.FC<TopupModalProps> = ({ open, packs, onClose, onGranted, api }) => {
  const [stage, setStage] = useState<Stage>('select');
  const [selected, setSelected] = useState<CreditPack | null>(packs[0] ?? null);
  const [errorText, setErrorText] = useState('');
  const [notice, setNotice] = useState('');
  const [profile, setProfile] = useState<BillingProfile | null>(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const pollCancelledRef = useRef(false);
  const sinceRef = useRef<Date>(new Date());
  const { openCheckout } = useRazorpayCheckout();

  const resolveApi = useCallback(async (): Promise<TopupModalApi> => {
    if (api) return api;
    const [billing, credits] = await Promise.all([
      import('../../services/billingApi'),
      import('../../services/creditsApi'),
    ]);
    return {
      createTopupCheckout: billing.billingApiClient.createTopupCheckout,
      getBillingProfile: billing.billingApiClient.getBillingProfile,
      saveBillingProfile: (value) =>
        billing.billingApiClient.saveBillingProfile({
          billing_name: value.billing_name.trim(),
          state_code: value.state_code,
          gstin: value.gstin.trim() || null,
          billing_address: value.billing_address.trim() || null,
        }),
      getMyCredits: async () => (await credits.creditsApi.getMyCredits()).data,
    };
  }, [api]);

  const pollForGrant = useCallback(async () => {
    setStage('granting');
    for (const delay of POLL_DELAYS_MS) {
      await sleep(delay);
      if (pollCancelledRef.current) return;
      try {
        const { getMyCredits } = await resolveApi();
        const credits = await getMyCredits();
        if (credits.batches === undefined) {
          // Nothing reliable to poll for on this backend — stop polling and
          // say so honestly rather than timing out on a predicate that can
          // never be true.
          if (!pollCancelledRef.current) {
            toast.success('Payment received — your credits will appear shortly.');
            onGranted?.();
            onClose();
          }
          return;
        }
        if (hasNewTopupBatch(credits, sinceRef.current)) {
          if (pollCancelledRef.current) return;
          toast.success('Your top-up credits are ready.');
          onGranted?.();
          onClose();
          return;
        }
      } catch (err) {
        // Transient errors during polling are swallowed — keep polling on
        // the same schedule rather than surfacing noise mid-poll.
        console.error('Credit poll failed:', err);
      }
    }
    if (!pollCancelledRef.current) setStage('timed_out');
  }, [resolveApi, onGranted, onClose]);

  const handleBuy = useCallback(async () => {
    if (!selected) return;
    setStage('processing');
    setErrorText('');
    setNotice('');
    sinceRef.current = new Date();
    try {
      const { createTopupCheckout } = await resolveApi();
      const session = await createTopupCheckout(selected.pack_code);
      await openCheckout(session.checkout_payload || {}, {
        onPaid: () => {
          pollForGrant();
        },
        onDismiss: () => {
          setNotice(
            'If you completed the payment, your credits will appear within a couple of ' +
              'minutes — you do not need to pay again.',
          );
          setStage('select');
        },
        onFailed: () => {
          toast.error('Payment failed — please try again.');
          setStage('select');
        },
      });
    } catch (err) {
      if (apiErrorDetail(err)?.code === 'billing_profile_required') {
        try {
          const { getBillingProfile } = await resolveApi();
          setProfile(await getBillingProfile());
          setDetailsError('');
          setStage('details');
          return;
        } catch (profileErr) {
          console.error('Billing profile load failed:', profileErr);
        }
      }
      console.error('Topup checkout failed:', err);
      const message = checkoutErrorMessage(err, TOPUP_ERROR_OVERRIDES);
      setErrorText(message);
      setStage('select');
      toast.error(message);
    }
  }, [selected, resolveApi, pollForGrant, openCheckout]);

  const handleSaveDetails = async (value: BillingDetailsValue) => {
    setSavingDetails(true);
    setDetailsError('');
    try {
      const { saveBillingProfile } = await resolveApi();
      await saveBillingProfile(value);
      setSavingDetails(false);
      await handleBuy();
    } catch (err) {
      setSavingDetails(false);
      const code = apiErrorDetail(err)?.code;
      setDetailsError((code && DETAILS_ERROR[code]) || 'Could not save your details. Please try again.');
    }
  };

  const handleClose = () => {
    pollCancelledRef.current = true;
    onClose();
  };

  const title =
    stage === 'granting'
      ? 'Adding your credits'
      : stage === 'timed_out'
        ? 'Payment received'
        : stage === 'details'
          ? 'Invoice details'
          : 'Buy AI credits';

  return (
    <Modal open={open} onClose={handleClose} title={title} size="md">
      {stage === 'select' && (
        <div className="space-y-4">
          {packs.length === 0 ? (
            <p className="text-sm text-parchment-muted-warm">
              No credit packs are on sale right now — please check back soon.
            </p>
          ) : (
            <div className="space-y-2">
              {packs.map((pack) => (
                <button
                  key={pack.pack_code}
                  type="button"
                  onClick={() => setSelected(pack)}
                  className={`w-full text-left px-3 py-2.5 border transition-colors ${
                    selected?.pack_code === pack.pack_code
                      ? 'border-brass bg-brass/10'
                      : 'border-parchment-border hover:bg-parchment'
                  }`}
                >
                  <span className="text-sm text-chamber-panel font-medium">
                    +{pack.credits} AI credits
                  </span>
                  <span className="block text-xs text-parchment-muted-warm mt-0.5">
                    {chargeSummary(pack)}
                  </span>
                </button>
              ))}
            </div>
          )}
          {selected && (
            <p className="text-xs text-parchment-muted-warm">
              These credits expire when your current plan period ends.
            </p>
          )}
          {notice && <p className="text-sm text-parchment-muted-warm">{notice}</p>}
          {errorText && <p className="text-sm text-crimson-600">{errorText}</p>}
          <div className="flex justify-end gap-2 pt-2 border-t border-parchment-border">
            <Button variant="ghost" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="brass" size="sm" disabled={!selected} onClick={() => void handleBuy()}>
              Continue to payment
            </Button>
          </div>
        </div>
      )}

      {stage === 'details' && profile && (
        <BillingDetailsForm
          states={profile.states}
          initial={{
            billing_name: profile.billing_name ?? '',
            state_code: profile.state_code ?? '',
            gstin: profile.gstin ?? '',
            billing_address: profile.billing_address ?? '',
          }}
          saving={savingDetails}
          error={detailsError}
          onSubmit={handleSaveDetails}
          onCancel={() => setStage('select')}
        />
      )}

      {stage === 'processing' && (
        <div className="py-10 flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brass" />
          <p className="text-sm text-parchment-muted-warm">Opening secure checkout…</p>
        </div>
      )}

      {stage === 'granting' && (
        <div className="py-10 flex flex-col items-center gap-3 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brass" />
          <p className="text-sm text-chamber-panel font-medium">Payment received — adding your credits…</p>
          <p className="text-xs text-parchment-muted-warm max-w-sm">
            This usually takes a few seconds. Please keep this window open.
          </p>
        </div>
      )}

      {stage === 'timed_out' && (
        <div className="py-10 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-chamber-panel font-medium">Payment received, credits are on the way.</p>
          <p className="text-xs text-parchment-muted-warm max-w-sm">
            This can take a minute — your balance updates automatically. You can safely close this
            window and keep working; refresh later if you don&apos;t see the change.
          </p>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            Close
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default TopupModal;
